/**
 * Theme context providing light/dark theme support for the Tourist Tools application.
 * Wraps the app tree (inside Router) and provides the active theme plus toggle controls.
 * Theme preference is persisted in localStorage under 'touristtools-theme'.
 * Must be placed INSIDE <Router>.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

// ============================================
// Types
// ============================================

export type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

// ============================================
// Context
// ============================================

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ============================================
// Provider
// ============================================

const STORAGE_KEY = 'touristtools-theme';

function getInitialTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
    // First visit: detect OS preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
  } catch {
    // localStorage may not be available in some environments
  }
  return 'light';
}

function applyThemeToDocument(theme: Theme): void {
  document.documentElement.setAttribute('data-theme', theme);
  // Update meta theme-color for mobile browser chrome
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute('content', theme === 'light' ? '#18DABF' : '#010E11');
  }
}

export function ThemeProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);

  const effectiveTheme: Theme = theme;

  function setTheme(t: Theme): void {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      // localStorage may not be available in some environments
    }
    setThemeState(t);
  }

  function toggleTheme(): void {
    setTheme(theme === 'light' ? 'dark' : 'light');
  }

  // Sync data-theme attribute whenever effectiveTheme changes
  useEffect(() => {
    applyThemeToDocument(effectiveTheme);
  }, [effectiveTheme]);

  // Remove the no-transitions class added by the FOWT inline script —
  // this allows CSS transitions to activate after the initial paint
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      document.documentElement.classList.remove('no-transitions');
    });
    return () => cancelAnimationFrame(raf);
  }, []); // runs once on mount

  const value: ThemeContextValue = {
    theme,
    setTheme,
    toggleTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

// ============================================
// Hook
// ============================================

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
